import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/**
 * Opens a URL in the device's external browser
 *
 * - Native (iOS/Android): Opens in Safari/Chrome using Capacitor Browser plugin
 * - Web: Opens in new tab
 *
 * Use this for:
 * - Product links with affiliate tracking
 * - External resources
 * - Any URL that should open outside the app
 *
 * @param url - Full URL to open (must include protocol)
 * @returns Promise that resolves when URL is opened
 *
 * @example
 * ```ts
 * await openExternalUrl('https://nike.com/shoes');
 * ```
 */
export const openExternalUrl = async (url: string): Promise<void> => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Native: use Capacitor Browser plugin to open in system browser
      await Browser.open({ url });
    } else {
      // Web: open in new tab
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Popup blocked - navigate directly
        window.location.href = url;
      }
    }
  } catch (error) {
    console.error('Failed to open external URL:', error);
    // Fallback to window.open on error
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};
